import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUpdateQuestion } from "@/hooks/useQuestions";
import { L } from "@/i18n";
import { OptionsEditor } from "./OptionsEditor";

const input = "w-full h-9 rounded-md bg-[#0f172a] border border-slate-700 px-3 text-sm text-slate-100 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const area = `${input} h-auto py-2 leading-relaxed`;
const DIFFICULTIES = [["easy", "Easy", "Fácil"], ["medium", "Medium", "Média"], ["hard", "Hard", "Difícil"]];

const toForm = (q) => ({
  question_text: q?.question_text ?? "",
  options: [...(q?.options ?? [])],
  correct_option: q?.correct_option ?? 0,
  explanation: q?.explanation ?? "",
  difficulty: q?.difficulty || "medium",
});

const Field = ({ label, children }) => (
  <div>
    <p className="mb-1.5 text-xs font-medium text-slate-300">{label}</p>
    {children}
  </div>
);

export const EditQuestionDialog = ({ question, onClose }) => {
  const update = useUpdateQuestion();
  const [form, setForm] = useState(toForm(question));
  const [error, setError] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const locked = (question?.option_images ?? []).some(Boolean);

  useEffect(() => { setForm(toForm(question)); setError(""); }, [question]);

  const close = (open) => !open && !update.isPending && onClose();

  const submit = (e) => {
    e.preventDefault();
    const options = form.options.map((o) => o.trim());
    if (!form.question_text.trim()) return setError(L("Question text is required.", "O texto da pergunta é obrigatório."));
    if (options.length < 2 || options.some((o) => !o)) return setError(L("All options must be filled (minimum 2).", "Todas as opções devem estar preenchidas (mínimo 2)."));
    const changes = { ...form, question_text: form.question_text.trim(), explanation: form.explanation.trim(), options };
    update.mutate({ id: question.id, changes }, { onSuccess: onClose });
  };

  return (
    <Dialog open={!!question} onOpenChange={close}>
      <DialogContent className="bg-[#131f37] border-[#1e293b] text-slate-200 sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-question-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-50">{L("Edit question", "Editar pergunta")}</DialogTitle>
          <DialogDescription className="font-mono text-[11px] text-slate-500">{question?.module_title} · {question?.id}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5" noValidate>
          <Field label={L("Question text *", "Texto da pergunta *")}>
            <textarea rows={3} className={area} value={form.question_text} onChange={set("question_text")} data-testid="edit-question-text" />
          </Field>
          <Field label={L("Options *", "Opções *")}>
            <OptionsEditor options={form.options} correct={form.correct_option} onChange={set("options")} onCorrect={set("correct_option")} locked={locked} inputCls={input} />
          </Field>
          <Field label={L("Explanation", "Explicação")}>
            <textarea rows={3} className={area} value={form.explanation} onChange={set("explanation")} data-testid="edit-question-explanation" />
          </Field>
          <Field label={L("Difficulty", "Dificuldade")}>
            <Select value={form.difficulty} onValueChange={set("difficulty")}>
              <SelectTrigger className="h-9 w-48 bg-[#0f172a] border-slate-700 text-slate-200" data-testid="edit-question-difficulty"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0f172a] border-slate-700 text-slate-200">
                {DIFFICULTIES.map(([v, en, pt]) => <SelectItem key={v} value={v} className="focus:bg-slate-800 focus:text-slate-50" data-testid={`edit-question-difficulty-${v}`}>{L(en, pt)}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {error && <p className="text-sm text-rose-300" data-testid="edit-question-error">{error}</p>}
          <DialogFooter>
            <button type="button" onClick={onClose} disabled={update.isPending} className="rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800" data-testid="edit-question-cancel">{L("Cancel", "Cancelar")}</button>
            <button type="submit" disabled={update.isPending} className="flex items-center justify-center gap-2 rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-[#0a1425] hover:bg-amber-400 disabled:opacity-60" data-testid="edit-question-save">
              {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {L("Save changes", "Guardar alterações")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
